// 시각(ms) ↔ 픽셀 계산과 쌓기·묶기 — uPlot·React·DOM 을 모른다.
//
// 이 파일을 따로 두는 이유: 「막대가 그려지는가」를 브라우저 없이 node --test 로 검사하려면
// 그림의 모양(어느 칸에 몇 px 폭으로 몇 px 높이)을 정하는 계산이 그리는 코드와 떨어져
// 있어야 한다. 계기가 된 사고(store-mops 리소스 부하 막대가 안 보임)는 화면은 뜨고 툴팁도
// 나오는데 막대 폭만 0 이던 일이라, 눈으로 보기 전에는 아무도 몰랐다.

// KINDS 는 시리즈 하나를 그리는 모양이다.
//
//   bar  — 버킷마다 막대. 칸이 넓을 때 값 하나하나를 읽기 좋다
//   area — 막대를 틈 없이 붙인 계단 면. 칸이 좁을 때 막대 사이 틈이 무늬(모아레)로 번지지 않는다
//   line — 선. 여러 계열을 겹쳐 비교할 때
//   auto — 보는 구간에 따라 bar / area 를 고른다(pickKind)
export const KINDS = ['bar', 'area', 'line', 'auto']

// AUTO_BAR_MIN_PX 는 auto 가 막대를 고르는 버킷 한 칸의 최소 폭(CSS px)이다.
//
// 3 인 이유: 막대 사이 틈이 최소 1px 은 나와야 막대로 읽힌다. 2px 칸에서 틈을 1px 주면
// 막대가 1px 이 되어 틈과 막대가 번갈아 줄무늬가 되고, 값보다 무늬가 먼저 보인다.
export const AUTO_BAR_MIN_PX = 3

// BAR_GAP 은 막대 칸에서 틈이 차지하는 비율이다(최소 1px — binColumns).
export const BAR_GAP = 0.2

// PLOT_PAD_RIGHT 는 판 오른쪽 여백(px)이다. TimeChart 가 uPlot padding 으로 쓰고, 툴팁이
// 가로 자리를 잴 때 뺀다 — 판은 상자 오른쪽 끝까지 가지 않고 이만큼 앞에서 끝난다.
export const PLOT_PAD_RIGHT = 24

// X_AXIS_H 는 x축 글자 줄의 높이(px)다. TimeChart·RangeChart 가 같이 쓰고 밖으로도 내보낸다 —
// 서비스가 차트 옆에 손 라벨·빈 자리를 축 높이만큼 맞춰 세울 때 28 을 따로 적으면, 여기를 바꾸는
// 날 그 자리만 어긋난 채 남는다(에러 없이). 그래서 숫자 대신 이 이름을 문다.
export const X_AXIS_H = 28

// labelFits 는 x(판 왼쪽에서 CSS px)에 가운데 정렬로 앉을 축 글자가 잘리지 않는지 답한다.
// 글자는 판 오른쪽 여백(PLOT_PAD_RIGHT)까지는 넘어가도 되고, 그 너머는 캔버스 밖이라 잘린다.
//
// 폭은 글자 수로 어림한다 — 축 글꼴(--t-mono-sm)은 고정폭이라 한 칸이 0.5em(Sarasa)~0.6em
// (JetBrains 폴백)이고, 넓은 쪽(12px × 0.6)으로 잡아 잘릴 글자를 남기지 않는다. 예전에는
// 「끝에서 36px」 고정이었는데, 날짜가 붙은 「09.30 00:00」(반 폭 약 40px)이 그 틈으로 빠져
// 잘린 채 적혔다.
const AXIS_CHAR_PX = 12 * 0.6
export function labelFits(x, text, plotWidth) {
  return x + (String(text).length * AXIS_CHAR_PX) / 2 <= plotWidth + PLOT_PAD_RIGHT
}

// tooltipStyle 은 ChartTooltip 을 기준선 옆에 앉히는 CSS 자리를 낸다.
//
//   ratio   가리킨 시각이 판 가로의 어디쯤인가(0~1)
//   offset  판이 상자 왼쪽에서 떨어진 폭(px) — 보통 yAxisSize
//
// 판의 폭은 「상자 − 왼쪽 offset − 오른쪽 PLOT_PAD_RIGHT」다. 오른쪽 여백을 빼지 않으면
// 오른쪽으로 갈수록 툴팁이 기준선에서 밀려(끝에서 24px) 뒤집힌 툴팁이 기준선을 덮는다.
// 오른쪽 절반이면 왼쪽으로 뒤집어 상자 밖으로 안 나가게 한다.
export function tooltipStyle(ratio, offset = 0) {
  const plot = `(100% - ${offset + PLOT_PAD_RIGHT}px)`
  return ratio > 0.5
    ? { flip: true, style: { right: `calc(${PLOT_PAD_RIGHT}px + ${plot} * ${1 - ratio})` } }
    : { flip: false, style: { left: `calc(${offset}px + ${plot} * ${ratio})` } }
}

// bucketPx 는 버킷 한 칸이 판에서 몇 px 인지 답한다.
export function bucketPx(bucketMs, win, plotPx) {
  const span = win.to - win.from
  if (!(span > 0) || !(plotPx > 0)) return 0
  return (bucketMs / span) * plotPx
}

// pickKind 는 kind 가 auto 일 때 지금 보는 구간에 맞는 모양을 고른다. 다른 kind 는 그대로다.
//
// 부하와 읽기를 함께 보고 고른다 — 칸이 좁으면 막대 수천 개가 1px 로 뭉개져 어차피 면처럼
// 보이는데 틈 계산만 헛돈다. 면이면 픽셀 열 하나에 사각형 하나라 화면 폭만큼만 그린다.
export function pickKind(kind, bucketMs, win, plotPx) {
  if (kind !== 'auto') return kind
  return bucketPx(bucketMs, win, plotPx) >= AUTO_BAR_MIN_PX ? 'bar' : 'area'
}

// stackGroups 는 시리즈 선언을 쌓기 묶음으로 나눈다.
//
// 같은 stack 이름을 가진 시리즈가 한 묶음이고 **선언 순서대로 아래에서 위로** 쌓인다.
// stack 이 없으면 혼자 한 묶음이다. 돌려주는 것은 [{ key, members: [시리즈 번호…] }] 다.
export function stackGroups(series) {
  const groups = []
  const byName = new Map()
  series.forEach((s, idx) => {
    if (s.stack == null) { groups.push({ key: `#${idx}`, members: [idx] }); return }
    let g = byName.get(s.stack)
    if (!g) { g = { key: s.stack, members: [] }; byName.set(s.stack, g); groups.push(g) }
    g.members.push(idx)
  })
  return groups
}

// stackValues 는 각 시리즈의 값 배열을 **누적값**으로 바꾸고, 각 시리즈의 바닥 배열을 함께 준다.
//
// 반환: { tops: [배열…], bases: [배열|null…] } — 시리즈 번호 순서.
// 한 시각에 묶음의 모든 값이 null 이면 그 시각은 누적도 null 이다(「0 이었다」와 「표본이
// 없다」를 가르기 위해서다 — 0 으로 채우면 빈 구간이 바닥에 붙은 막대처럼 보인다).
// 묶음 안에서 일부만 null 이면 그 조각은 0 으로 친다.
export function stackValues(series, values) {
  const tops = values.map((v) => v)
  const bases = values.map(() => null)
  for (const g of stackGroups(series)) {
    if (g.members.length < 2) continue
    const n = Math.max(...g.members.map((m) => values[m].length))
    const cum = new Float64Array(n)
    const any = new Uint8Array(n)
    for (const m of g.members) {
      const src = values[m]
      for (let i = 0; i < n; i++) if (src[i] != null) any[i] = 1
    }
    let prev = null
    for (const m of g.members) {
      const src = values[m]
      const top = new Array(n)
      const base = new Array(n)
      for (let i = 0; i < n; i++) {
        if (!any[i]) { top[i] = null; base[i] = null; continue }
        base[i] = cum[i]
        cum[i] += src[i] ?? 0
        top[i] = cum[i]
      }
      tops[m] = top
      bases[m] = prev ? base : null
      prev = top
    }
  }
  return { tops, bases }
}

// binColumns 는 그릴 막대(또는 면의 조각)를 **픽셀 열 단위로** 고른다.
//
//   xs      버킷 시작 시각(ms) 배열
//   tops    묶음의 맨 위 값(누적값) 배열 — 이것으로 열의 대표를 고른다
//   i0, i1  보이는 첨자 범위(양끝 포함)
//   toPx    시각 → 캔버스 가로 좌표(device px). float64 로 계산해야 한다(아래)
//   colPx   버킷 한 칸의 폭(device px)
//   gap     틈 비율. area 면 0. 칸이 AUTO_BAR_MIN_PX 보다 좁으면 무시한다
//
// 반환: [{ i, x, w }] — i 는 그 열을 대표하는 버킷, x·w 는 device px.
//
// **한 픽셀 열에 버킷이 여럿 몰리면 가장 높은 하나만 남긴다.** 1분 버킷으로 30일을 보면
// 43,200 칸이 1,200px 에 들어간다 — 그대로 그리면 한 열에 36개가 겹쳐 그려지고, 쌓은
// 막대는 조각마다 다른 버킷의 최댓값이 섞여 「CPU 사용은 이 시각, iowait 은 저 시각」인
// 막대가 된다. 대표 하나를 골라 그 버킷의 조각만 쌓으면 모양이 늘 실제 한 시각이고,
// 그리는 사각형 수도 화면 폭을 넘지 않는다. 최댓값을 고르는 이유는 이 차트들이 찾는 것이
// 대개 치솟은 순간이라서다 — 평균으로 뭉개면 장애 구간이 사라진다.
//
// 폭은 **최소 1 device px** 이다. 계기가 된 사고가 막대 폭 0 이었다.
//
// 좌표 정밀도: epoch ms(약 1.8e12)를 그대로 SVG 좌표로 쓰면 브라우저가 float32 로 반올림해
// 약 131초 간격으로 뭉개진다. 여기서는 toPx 가 JS number(float64)로 픽셀까지 바꾼 뒤의
// 작은 수만 다루므로 그 문제가 구조적으로 없다.
export function binColumns({ xs, tops, i0, i1, toPx, colPx, gap = 0 }) {
  const out = []
  if (!xs || !xs.length) return out
  const lo = Math.max(0, i0 ?? 0)
  const hi = Math.min(xs.length - 1, i1 ?? xs.length - 1)
  // 틈은 막대로 읽힐 만큼 칸이 넓을 때(AUTO_BAR_MIN_PX 이상)만 두고, 두면 최소 1px 이다.
  // 비율만 보면 3~5px 칸에서 틈이 1px 이 안 돼 없어지고, 막대 모드인데 면처럼 보인다.
  const gapPx = gap > 0 && colPx >= AUTO_BAR_MIN_PX ? Math.max(1, colPx * gap) : 0
  const w = Math.max(1, colPx - gapPx)
  let last = null
  for (let i = lo; i <= hi; i++) {
    const v = tops[i]
    if (v == null) continue
    const x = toPx(xs[i]) + gapPx / 2
    const col = Math.floor(x)
    if (last && last.col === col) {
      if (v > tops[last.i]) last.i = i
      continue
    }
    last = { i, x, w, col }
    out.push(last)
  }
  // 칸이 1px 보다 좁으면 대표를 고른 열이 서로 이어 붙어야 면이 된다 — 폭 1 그대로면
  // 소수점 자리 때문에 열 사이에 머리카락 같은 틈이 생긴다. 열의 왼쪽 끝을 정수로 붙인다.
  if (colPx < 1) for (const c of out) { c.x = c.col; c.w = 1 }
  return out.map(({ i, x, w: cw }) => ({ i, x, w: cw }))
}

// clampWindow 는 보는 구간을 받아 둔 전체(extent) 안으로 민다. 폭은 지킨다 — 폭이 전체보다
// 넓으면 전체로 줄인다.
export function clampWindow(win, extent) {
  const full = extent.to - extent.from
  const span = Math.min(win.to - win.from, full)
  let from = win.from
  if (from < extent.from) from = extent.from
  if (from + span > extent.to) from = extent.to - span
  return { from, to: from + span }
}

// zoomWindow 는 anchor 시각을 제자리에 두고 구간을 factor 배로 늘리거나 줄인다.
//
// 버킷 넷보다 좁게는 안 줄인다 — 더 들어가 봐야 막대 몇 개가 판을 채울 뿐 새로 보이는
// 것이 없고, 어디를 보는지 잃기만 한다.
export function zoomWindow(win, extent, factor, anchor, bucketMs) {
  const span = win.to - win.from
  const min = Math.max(1, (bucketMs || 60000) * 4)
  const next = Math.max(min, Math.min(extent.to - extent.from, span * factor))
  const r = span > 0 ? (anchor - win.from) / span : 0.5
  const from = anchor - r * next
  return clampWindow({ from, to: from + next }, extent)
}

// ── 개관 띠의 보는 구간 창(onBandChange) ──
//
// 개관 띠는 전체 기간 위에 지금 보는 구간(band)을 칠해 둔다. 창을 끌어 옮기고 양끝을 끌어 폭을
// 바꾸려면 「누른 자리가 창의 어디인가」와 「끈 만큼 창이 어떻게 되는가」를 정해야 한다 — 그 셈을
// 여기 둔다(브라우저 없이 시험한다).

// BAND_EDGE_PX 는 창 양끝을 「손잡이」로 볼 폭(CSS px)이다. 마우스로 1px 선을 집기는 어렵고,
// 터치는 손가락이 굵어 더 넓어야 한다 — 그래서 부르는 쪽이 터치면 넓힌다(bandHit 의 edge).
export const BAND_EDGE_PX = 6

// bandHit 은 누른 자리(px)가 창의 어디인지 답한다 — 'from'(왼쪽 손잡이)·'to'(오른쪽 손잡이)·
// 'move'(창 안)·null(창 밖). x1·x2 는 창의 양끝(px)이다. 창이 손잡이 둘보다 좁으면 가운데를
// 'move' 로 남긴다 — 아주 좁게 확대한 창은 손잡이만 남아 옮길 수 없게 된다.
export function bandHit(px, x1, x2, edge = BAND_EDGE_PX) {
  if (!(x2 >= x1)) return null
  const w = x2 - x1
  const e = Math.min(edge, w / 3)
  if (px < x1 - edge || px > x2 + edge) return null
  if (px <= x1 + e) return 'from'
  if (px >= x2 - e) return 'to'
  return 'move'
}

// dragBand 는 처음 창(start)을 mode 대로 dt(ms)만큼 끈 창이다. 창은 extent 안에 가두고,
// 폭은 minSpan 아래로 안 줄인다(손잡이를 맞은편 너머로 끌면 그 자리에서 멈춘다 — 뒤집지 않는다).
export function dragBand(start, mode, dt, extent, minSpan = 0) {
  if (mode === 'move') return clampWindow({ from: start.from + dt, to: start.to + dt }, extent)
  if (mode === 'from') {
    const from = Math.max(extent.from, Math.min(start.from + dt, start.to - minSpan))
    return { from, to: start.to }
  }
  if (mode === 'to') {
    const to = Math.min(extent.to, Math.max(start.to + dt, start.from + minSpan))
    return { from: start.from, to }
  }
  return start
}

// nearestIndex 는 시각 t 가 들어 있는 버킷의 첨자를 찾는다(xs 는 오름차순). 없으면 -1.
//
// 버킷 사이 시각을 그대로 쓰지 않는 이유: 「이 값은 어느 칸의 값인가」가 흐려진다. 막대는
// 칸 단위로만 있으므로 읽는 자리도 칸이어야 한다.
export function nearestIndex(xs, t, bucketMs) {
  let lo = 0
  let hi = xs.length - 1
  if (hi < 0) return -1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (xs[mid] <= t) lo = mid
    else hi = mid - 1
  }
  if (xs[lo] <= t && t < xs[lo] + bucketMs) return lo
  return -1
}

// bandPairs 는 `bands` prop([{ lower, upper, color }] — 계열 key 로 적는다)을 시리즈 번호
// 짝으로 바꾼다. 없는 key 를 가리키는 띠는 **조용히 버린다** — 그리던 판 하나가 띠 설정
// 하나 때문에 통째로 안 서면 안 되고, 기준선이 아직 안 들어온 자료(옛 규격)도 흔하다.
// 위·아래가 같은 계열이면 띠의 폭이 0 이라 버린다.
//
// 반환: [{ upper, lower, color }] — upper·lower 는 series 배열의 0 부터 번호.
export function bandPairs(series, bands) {
  const at = new Map(series.map((s, i) => [s.key, i]))
  const out = []
  for (const b of bands || []) {
    const upper = at.get(b.upper)
    const lower = at.get(b.lower)
    if (upper == null || lower == null || upper === lower) continue
    out.push({ upper, lower, color: b.color })
  }
  return out
}

// outsideIndices 는 값이 하한 아래이거나 상한 위인 첨자를 고른다(i0~i1, 양끝 포함).
//
// 행마다 바뀌는 기준선(예: 이상감지의 aly_min_per·aly_max_per)과 견준다 — 고정 기준선이면
// `lines`·`over` 로 충분하다. **기준선이 비어 있는 행은 그쪽으로는 견주지 않는다**: 「하한을
// 모른다」를 「하한 0」으로 치면 정상 값이 넘은 것처럼 칠해진다. 값이 비면 점도 없다.
export function outsideIndices(vals, lower, upper, i0 = 0, i1 = (vals?.length ?? 0) - 1) {
  const out = []
  if (!vals) return out
  const lo = Math.max(0, i0)
  const hi = Math.min(vals.length - 1, i1)
  for (let i = lo; i <= hi; i++) {
    const v = vals[i]
    if (v == null) continue
    const l = lower ? lower[i] : null
    const u = upper ? upper[i] : null
    if ((l != null && v < l) || (u != null && v > u)) out.push(i)
  }
  return out
}

// bandSegments 는 띠(두 계열 사이)를 칠할 구간을 첨자 묶음으로 나눈다. 위·아래 중 하나라도
// 빈 행에서 **띠를 끊는다** — 이어 칠하면 「기준선이 없던 시간」이 앞뒤 값 사이의 직선
// 기준선이 있던 것처럼 보인다. 점 하나뿐인 묶음은 넓이가 없어 버린다.
export function bandSegments(upper, lower, i0 = 0, i1 = (upper?.length ?? 0) - 1) {
  const out = []
  if (!upper || !lower) return out
  let cur = []
  const flush = () => { if (cur.length > 1) out.push(cur); cur = [] }
  for (let i = Math.max(0, i0); i <= Math.min(upper.length - 1, i1); i++) {
    if (upper[i] == null || lower[i] == null) flush()
    else cur.push(i)
  }
  flush()
  return out
}

// lineSeriesOptions 는 `kind: 'line'` 계열의 uPlot 계열 옵션을 만든다. stroke 는 색을 그 순간
// 토큰에서 푸는 함수다(테마 전환을 따라가게 값이 아니라 함수로 넘긴다).
//
// dash — 기준선처럼 읽혀야 하는 계열(행마다 바뀌는 하한·상한)은 점선·가는 선이다. 값 계열과
// 같은 실선이면 셋 중 무엇이 값인지 색으로만 갈라야 한다. dash 가 없으면 키 자체를 안 둔다 —
// 기존 실선 계열의 옵션이 이 추가 전과 똑같이 남게(「기존 prop 동작 불변」).
export const LINE_DASH = [4, 4]
export function lineSeriesOptions(s, stroke) {
  return {
    label: s.label, scale: 'y', stroke, width: s.dash ? 1 : 1.5, points: { show: false },
    ...(s.dash ? { dash: LINE_DASH } : {}),
  }
}
